import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  Bot, Send, Loader, ArrowLeft, Sparkles, MessageSquare, 
  FileText, CheckCircle, HelpCircle, Trash2, Upload
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConversationalEstimate = () => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const chatEndRef = useRef(null);

  // Initial greeting
  useEffect(() => {
    const initialMessage = {
      role: 'assistant',
      content: `👋 Hi there! I'm your PSM Welding project assistant.

I'm here to help you get a quote for your welding or fabrication project. Just tell me about what you need, and I'll ask a few questions to understand your project better.

**To get started, you can:**
• Describe your project in your own words
• Tell me what type of work you need
• Share any specific requirements or timeline

What project can I help you with today?`,
      timestamp: new Date().toISOString()
    };
    setMessages([initialMessage]);
    setSuggestions([
      "I need a custom bracket fabricated",
      "I have a trailer that needs repair",
      "I need some aluminum welding done"
    ]);
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (messageText = inputValue) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);
    setSuggestions([]);

    try {
      const response = await axios.post(`${API}/ai/chat/estimate`, {
        message: messageText,
        session_id: sessionId,
        estimate_context: extractedData
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Update extracted data if any
      if (response.data.extracted_data) {
        setExtractedData(prev => ({...prev, ...response.data.extracted_data}));
      }

      // Update suggestions
      if (response.data.suggestions) {
        setSuggestions(response.data.suggestions);
      }

    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message. Please try again.');
      
      const errorMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an issue. Could you please try again?",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleSubmitEstimate = async () => {
    if (Object.keys(extractedData).length === 0) {
      toast.error('Please provide more project details before submitting.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/chat/session/${sessionId}/create-estimate`);
      toast.success('Estimate request submitted successfully!');
      navigate('/portal');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit estimate');
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = async () => {
    try {
      await axios.delete(`${API}/ai/chat/session/${sessionId}`);
      setMessages([{
        role: 'assistant',
        content: "Chat cleared! Let's start fresh. What welding or fabrication project can I help you with?",
        timestamp: new Date().toISOString()
      }]);
      setExtractedData({});
      setSuggestions([
        "I need a custom bracket fabricated",
        "I have a trailer that needs repair",
        "I need some aluminum welding done"
      ]);
      toast.success('Chat cleared');
    } catch (error) {
      console.error('Clear error:', error);
    }
  };

  const hasEnoughData = () => {
    return extractedData.project_type || extractedData.description;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="conversational-estimate-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/portal')}
                className="font-mono uppercase text-xs"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Sparkles className="text-primary" size={20} />
                <h1 className="text-lg font-bold uppercase tracking-tight">
                  AI Estimate Assistant
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearChat}
                className="text-muted-foreground hover:text-foreground"
              >
                <Trash2 size={16} className="mr-1" />
                Clear
              </Button>
              {hasEnoughData() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSummary(!showSummary)}
                  className="font-mono uppercase text-xs"
                >
                  <FileText size={16} className="mr-1" />
                  {showSummary ? 'Hide' : 'View'} Summary
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Chat Area */}
          <div className="lg:col-span-2">
            <Card className="border-border h-[calc(100vh-250px)] flex flex-col">
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="text-primary" size={16} />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] p-4 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                      <div className={`text-xs mt-2 ${msg.role === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="text-primary" size={16} />
                    </div>
                    <div className="bg-muted p-4 rounded-lg">
                      <Loader className="animate-spin" size={18} />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Suggestions */}
              {suggestions.length > 0 && !loading && (
                <div className="px-4 pb-2">
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-3 py-1.5 text-xs bg-primary/5 hover:bg-primary/10 border border-primary/20 rounded-full transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !loading && handleSend()}
                    placeholder="Describe your project or ask a question..."
                    disabled={loading}
                    className="text-sm"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={() => handleSend()}
                    disabled={!inputValue.trim() || loading}
                    className="px-4"
                    data-testid="send-button"
                  >
                    <Send size={18} />
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Side Panel */}
          <div className="space-y-4">
            {/* Project Summary Card */}
            {(showSummary || hasEnoughData()) && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-mono uppercase tracking-wide flex items-center gap-2">
                    <FileText size={16} />
                    Project Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.keys(extractedData).length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      As we chat, I'll gather your project details here.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {extractedData.project_type && (
                        <div>
                          <p className="text-xs text-muted-foreground">Type</p>
                          <p className="text-sm font-medium">{extractedData.project_type}</p>
                        </div>
                      )}
                      {extractedData.description && (
                        <div>
                          <p className="text-xs text-muted-foreground">Description</p>
                          <p className="text-sm">{extractedData.description}</p>
                        </div>
                      )}
                      {extractedData.dimensions && (
                        <div>
                          <p className="text-xs text-muted-foreground">Dimensions</p>
                          <p className="text-sm">{extractedData.dimensions}</p>
                        </div>
                      )}
                      {extractedData.materials && (
                        <div>
                          <p className="text-xs text-muted-foreground">Materials</p>
                          <p className="text-sm">{extractedData.materials}</p>
                        </div>
                      )}
                      {extractedData.timeline && (
                        <div>
                          <p className="text-xs text-muted-foreground">Timeline</p>
                          <p className="text-sm">{extractedData.timeline}</p>
                        </div>
                      )}
                      {extractedData.budget_range && (
                        <div>
                          <p className="text-xs text-muted-foreground">Budget</p>
                          <p className="text-sm">{extractedData.budget_range}</p>
                        </div>
                      )}
                      {extractedData.estimated_cost_range && (
                        <div className="pt-2 border-t border-border">
                          <p className="text-xs text-muted-foreground">Estimated Cost</p>
                          <p className="text-sm font-bold text-primary">{extractedData.estimated_cost_range}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Submit Button */}
            {hasEnoughData() && (
              <Button
                onClick={handleSubmitEstimate}
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
                data-testid="submit-estimate-btn"
              >
                <CheckCircle size={18} className="mr-2" />
                Submit Estimate Request
              </Button>
            )}

            {/* Help Card */}
            <Card className="border-border bg-muted/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="text-muted-foreground mt-0.5" size={18} />
                  <div>
                    <p className="text-xs font-semibold mb-1">How this works</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Just chat naturally about your project. I'll ask questions to understand your needs and provide rough cost estimates. When you're ready, submit your request and our team will follow up with a detailed quote.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-mono uppercase tracking-wide">
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-xs"
                  onClick={() => handleSend("What services does PSM Welding offer?")}
                >
                  <MessageSquare size={14} className="mr-2" />
                  View Services
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-xs"
                  onClick={() => handleSend("How much does welding typically cost?")}
                >
                  <MessageSquare size={14} className="mr-2" />
                  Pricing Info
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-xs"
                  onClick={() => handleSend("What materials can you work with?")}
                >
                  <MessageSquare size={14} className="mr-2" />
                  Materials Info
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationalEstimate;
